import React from "react";
import DocumentsTable from "../../../../components/Docs/DocumentsTable";
import Applet from "../../Applet";

// The documents list as the suite's standard grid.
//
// COLUMNS CHOSEN FOR THE QUESTION A READER IS ASKING: which document, what revision, what state,
// who owns it, and when it last moved. The preview is deliberately NOT a column -- it is what the
// grid view is for, and a wall of truncated prose would push the name off the row.
class DocumentTableApplet extends Applet {

    render() {
        return <DocumentsTable applet={this}
                               enterpriseComponent={this.props.enterpriseComponent}
                               columns={this.state.Columns}
                               placement={this.state.Placement}
                               pageSize={this.state.PageSize}
                               menu={this.state.Menu}
                               // The table draws its own "Documents (23)" heading and its own
                               // Refresh menu, so the page does not draw a second pair above it.
                               title="Documents"
                               rowHeight={this.state.RowHeight}
                               // The click that opens a document. It comes from the page rather
                               // than being implemented here, because the grid and the cards must
                               // do the same thing on the same action and the page is the one place
                               // that holds the refresh afterwards.
                               onOpen={this.props.onOpen}
                               onRefresh={this.props.onRefresh}
                               // NO TABLE SEARCH, and that is a product choice before it is a
                               // technical one: the page carries a real full-text search over every
                               // document's prose, and a second, weaker box inside the grid -- which
                               // filters only the rows already fetched -- would be two searches with
                               // different answers and no way to tell which you used.
                               //
                               // It is also what keeps a broken import out of the build.
                               // enableSearch pulls in SimpleSearchFormApplet -> PlatformDetail ->
                               // PlatformLongSelect, which imports an applet that does not exist in
                               // this repo OR in cres-t4, where it was cloned from. t4 builds only
                               // because nothing there ever enables search on a table.
                               enableSearch={false}
                               // THE SORT IS THE TABLE'S, like every other list in the suite: its
                               // menu, its remembered choice, its direction control on the column
                               // header. The grid follows it rather than competing with it -- the
                               // component reports the chosen specification back to the page, which
                               // re-sorts the one list both views are drawn from.
                               sort={this.state.Sort}
        />;
    }

    state = {
        Name: "DocumentTableApplet",
        // THE SAME SINGLE OPTION t1's object list carries. Per-document actions live on the row's
        // own menu, where they belong; this one acts on the list.
        Menu: {
            options: [
                {displayValue: "Refresh", value: "Refresh"},
            ],
        },
        // THE SAME FIVE ORDERS THE PAGE OFFERED, in the shape PlatformTable declares them.
        //
        // DRAFTS FIRST IS THE DEFAULT because this is a page for working on documents: a draft is
        // the one you can still change. It is `Status` as the attribute rather than a pseudo-field,
        // and the comparator in util/documentFilters is what gives it its meaning.
        Sort: [
            {Identifier: "Drafts first", Visible: true, Default: true,
             Options: [{Direction: "ASC", SortAttribute: "DRAFTS", DisplayName: "Drafts first"}]},
            {Identifier: "Object ID", Visible: true, Default: false,
             Options: [{Direction: "ASC", SortAttribute: "ObjectID", DisplayName: "Object ID"}]},
            {Identifier: "Name", Visible: true, Default: false,
             Options: [{Direction: "ASC", SortAttribute: "Name", DisplayName: "Name"}]},
            // NEWEST FIRST for both dates: "when was this created" is almost always asked as
            // "what is new". Reversible from the header, for the one time it is not.
            {Identifier: "Created", Visible: true, Default: false,
             Options: [{Direction: "DESC", SortAttribute: "CreatedDatetime", DisplayName: "Created"}]},
            {Identifier: "Last modified", Visible: true, Default: false,
             Options: [{Direction: "DESC", SortAttribute: "LastModifiedDatetime",
                        DisplayName: "Last modified"}]},
        ],
        RowHeight: 48,
        PageSize: {"default": 40, "xs": 3, "sm": 3, "md": 40, "lg": 40, "lx": 40},
        Placement: {
            "xl": [
                {id: "ObjectID"},
                {id: "Name"},
                {id: "DisplayRevision"},
                {id: "PrimaryPositionName"},
                {id: "LastModifiedDatetime"},
                {id: "Status"},
            ],
            // A PHONE GETS THE NUMBER, THE NAME AND THE STATE. Owner and modified time are what you
            // read when comparing documents; on a 390px screen they push the name out, and the name
            // is the only column nobody can work without.
            "xs": [
                {id: "ObjectID"},
                {id: "Name"},
                {id: "Status"},
            ],
        },
        // THE WIDTHS ADD UP TO 1030 SHOWN, 1120 WITH THE REVISION, and the container is wider than
        // both -- which is a constraint rather than a preference: the table does not scroll
        // horizontally, so a total wider than the page silently cuts the last column off the right
        // edge, and the last column is now the one carrying the document's state.
        Columns: [
            // THE NUMBER FIRST, because it is what a document is CALLED. "SOP-00202" is what people
            // say to each other, write on a form and search for; the title is how they recognise it
            // once they have found it. Every other list in the suite leads with the number.
            {id: "ObjectID", width: 130, cellRenderer: "Standard Single Cell", header: "enabled",
             headerRenderer: "Standard Single Header", headerTitle: "Document", hideable: false},
            {id: "Name", width: 360, cellRenderer: "Document Name Cell", header: "enabled",
             headerRenderer: "Standard Single Header", headerTitle: "Name", hideable: false},
            // OPTIONAL, AND OFF BY DEFAULT. Nearly every document in a list is at its current
            // revision, so the column is the same value repeated down the page -- worth having when
            // you are comparing revisions and worth the width back when you are not. `show: false`
            // keeps it in the Columns menu, one click away.
            {id: "DisplayRevision", width: 90, cellRenderer: "Standard Single Cell", header: "enabled",
             headerRenderer: "Standard Single Header", headerTitle: "Revision", show: false},
            {id: "PrimaryPositionName", width: 200, cellRenderer: "Standard Single Cell", header: "enabled",
             headerRenderer: "Standard Single Header", headerTitle: "Owner"},
            {id: "LastModifiedDatetime", width: 200, cellRenderer: "Local Datetime Cell", header: "enabled",
             headerRenderer: "Standard Single Header", headerTitle: "Modified"},
            // LAST, because it is the thing you scan DOWN rather than read across: a column of
            // pills at the right edge is one glance, where the same pills in the middle of the row
            // have to be found again on every line.
            {id: "Status", width: 140, cellRenderer: "Status Pill Cell", header: "enabled",
             headerRenderer: "Standard Single Header", headerTitle: "Status", hideable: false},
        ],
    };
}

export default DocumentTableApplet;
